import React, { useState } from 'react';
import { Container, Typography, Box, Grid, Card, CardContent, Button, Modal, Fade, Backdrop, CardMedia } from '@mui/material';
import CommonSection from '../components/CommonSection';

// Importing Material-UI icons
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import AssessmentIcon from '@mui/icons-material/Assessment';
import BusinessIcon from '@mui/icons-material/Business';
import PaymentsIcon from '@mui/icons-material/Payments';
import GavelIcon from '@mui/icons-material/Gavel';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
import SearchIcon from '@mui/icons-material/Search';

// Service data with icons and images
const servicesData = [
  {
    title: 'Accounting Services',
    description: 'We provide a full suite of accounting services to keep your financial records accurate and up-to-date.',
    services: [
      'Bookkeeping and Maintenance of accounts',
      'Extract trial Balances & Preparation of Statements',
      'Preparation of Bank Reconciliation',
      'Financial & Management Reports Preparation',
      'Preparation & Submission of VAT, NSSF, SHIF, NITA'
    ],
    icon: <AccountBalanceIcon sx={{ fontSize: 40, color: '#fdaa01' }} />,
    image: '../assets/img/dash.jpg', // Replace with the actual image path
  },
  {
    title: 'Auditing & Assurance',
    description: 'Get a thorough review of your financial practices with our auditing services.',
    services: [
      'Risk & Governance Assurance',
      'Internal Control & Process Assurance',
      'Regulatory & Ethical Compliance Assurance',
      'Assurance on capital market transactions',
      'Non-Statutory Audits',
      'Internal Audit Services'
    ],
    icon: <AssessmentIcon sx={{ fontSize: 40, color: '#fdaa01' }} />,
    image: '../assets/img/audit.jpeg', // Replace with the actual image path
  },
  {
    title: 'Business Advisory',
    description: 'Grow your business with our strategic advisory services.',
    services: [
      'Financial Planning and Management',
      'Operations and Process Improvements',
      'Human Resource Management',
      'Strategic Planning and Management',
      'Technology and Systems Advisory'
    ],
    icon: <BusinessIcon sx={{ fontSize: 40, color: '#fdaa01' }} />,
    image: '../assets/img/advisory.png', // Replace with the actual image path
  },
  {
    title: 'Payroll Services',
    description: 'Streamline your payroll processes with our comprehensive payroll services.',
    services: [
      'Payroll Advisory Services',
      'Payroll Processing',
      'Electronic Payment of Salaries to employees',
      'Statutory Compliance',
      'Provision of payroll reports & analysis in various formats',
      'Prompt response to all queries & complete confidentiality'
    ],
    icon: <PaymentsIcon sx={{ fontSize: 40, color: '#fdaa01' }} />,
    image: '../assets/img/down.jpeg', // Replace with the actual image path
  },
  {
    title: 'Tax Services',
    description: 'Stay compliant with the latest tax regulations with our expert tax services.',
    services: [
      'Tax Planning and Strategy Development',
      'Tax Compliance Services',
      'Tax Advisory Services',
      'Tax Investigation and Audit Support',
      'Transactional Tax Advisory Services'
    ],
    icon: <GavelIcon sx={{ fontSize: 40, color: '#fdaa01' }} />,
    image: '../assets/img/qwe.jpg', // Replace with the actual image path
  },
  {
    title: 'Technology & Systems Assurance',
    description: 'Ensure the reliability of your technology systems with our assurance services.',
    services: [
      'IT Assurance and Compliance',
      'Business Continuity and Disaster Recovery Planning',
      'Cybersecurity Assessments and Testing',
      'Data Privacy and Protection Services',
      'Cloud and Digital Transformation Advisory'
    ],
    icon: <VerifiedUserIcon sx={{ fontSize: 40, color: '#fdaa01' }} />,
    image: '../assets/img/tech.jpeg', // Replace with the actual image path
  },
  {
    title: 'Forensics Investigation Services',
    description: 'Uncover fraud and protect your business with our forensic investigation services.',
    services: [
      'Forensic Investigations',
      'Fraud Detection and Prevention',
      'Financial Audits and Reviews',
      'Expert Witness Testimony',
      'Risk Assessments and Management'
    ],
    icon: <SearchIcon sx={{ fontSize: 40, color: '#fdaa01' }} />,
    image: '../assets/img/qwert.jpeg', // Replace with the actual image path
  },
];


const Services = ({ darkMode }) => {
  const [open, setOpen] = useState(false);
  const [selectedService, setSelectedService] = useState(null);

  const handleOpen = (service) => {
    setSelectedService(service);
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setSelectedService(null);
  };

  return (
    <>
      <CommonSection title="Our Services" />

      <Container>
        <Box py={5}>
          <Typography variant="h4" align="center" gutterBottom color={darkMode ? '#fff' : '#000'}>
            Explore Our Financial Services
          </Typography>
          <Grid container spacing={4}>
            {servicesData.map((service, index) => (
              <Grid item xs={12} md={4} key={index}>
                <Card
                  sx={{
                    backgroundColor: darkMode ? '#333' : '#f5f5f5',
                    color: darkMode ? '#fff' : '#000',
                    transition: 'transform 0.3s',
                    '&:hover': {
                      transform: 'scale(1.05)',
                    },
                  }}
                  onClick={() => handleOpen(service)}
                >
                  {/* Card Media with image */}
                  <CardMedia
                    component="img"
                    height="140"
                    image={service.image}
                    alt={service.title}
                  />

                  <CardContent>
                    {/* Icon */}
                    {service.icon}
                    <Typography variant="h6" gutterBottom>
                      {service.title}
                    </Typography>
                    <Typography variant="body2">
                      {service.description}
                    </Typography>
                    <Button
                      variant="outlined"
                      sx={{
                        mt: 2,
                        color: darkMode ? '#fdaa01' : '#010536',
                        borderColor: darkMode ? '#fdaa01' : '#010536',
                        '&:hover': {
                          borderColor: darkMode ? '#fdaa01' : '#010536',
                        },
                      }}
                    >
                      Learn More
                    </Button>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>

          {/* Modal for service details */}
          {selectedService && (
            <Modal
              open={open}
              onClose={handleClose}
              closeAfterTransition
              BackdropComponent={Backdrop}
              BackdropProps={{
                timeout: 500,
              }}
            >
              <Fade in={open}>
                <Box
                  sx={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    width: 400,
                    bgcolor: darkMode ? '#222' : '#fff',
                    color: darkMode ? '#fff' : '#000',
                    boxShadow: 24,
                    p: 4,
                    borderRadius: '8px',
                  }}
                >
                  <Typography variant="h5" mb={2}>
                    {selectedService.title}
                  </Typography>
                  <Typography variant="body1" paragraph>
                    {selectedService.description}
                  </Typography>
                  <Typography variant="h6" mb={1}>
                    Services Offered:
                  </Typography>
                  <ul>
                    {selectedService.services.map((item, idx) => (
                      <li key={idx}>
                        <Typography variant="body2">{item}</Typography>
                      </li>
                    ))}
                  </ul>
                  <Button onClick={handleClose} variant="contained" sx={{ mt: 2 }}>
                    Close
                  </Button>
                </Box>
              </Fade>
            </Modal>
          )}
        </Box>
      </Container>
    </>
  );
};

export default Services;
